import { DeleteListItem } from "@/components/list-item/DeleteListItem.tsx";
import { ServerErrorField } from "@/components/server-error-field/ServerErrorField.tsx";
import {
  useApprovalCategoryDelete,
  useApprovalCategorySave,
} from "@/queries/approval/use-approval-categories.query.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import { ApiApprovalCategory } from "@/types/approvals/approval-category.type.ts";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Button,
  ConfirmationModal,
  Input,
  Modal,
  ModalProps,
  Stack,
  Textarea,
  useModals,
} from "@skailer-com/uikit";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

export interface ApprovalCategoryEditModalProps extends ModalProps {
  category?: ApiApprovalCategory;
}

export function ApprovalCategoryEditModal({ category, ...props }: ApprovalCategoryEditModalProps) {
  const { id } = { ...props };
  const { t } = useTranslation();
  const { close } = useModals();
  const modalsService = useModals();

  const isEdit = !!category?.id;

  const categorySchema = z.object({
    title: z.string().min(1, t("errors:field_required")),
    description: z.string().nullable(),
  });

  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    mode: "onSubmit",
    resolver: zodResolver(categorySchema),
    defaultValues: {
      title: category?.title ?? "",
      description: category?.description ?? "",
    },
  });

  const categorySave = useApprovalCategorySave(control);
  const categoryDelete = useApprovalCategoryDelete();

  const onSubmit = handleSubmit(async (data) => {
    const result = await categorySave.mutateAsync({
      id: category?.id ?? 0,
      payload: {
        title: data.title,
        description: data.description || null,
      },
    });

    analyticsService.trackEvent(
      isEdit ? analyticEvents.approvals.categoryEdited : analyticEvents.approvals.categoryCreated,
      { [analyticProperties.id]: result.id }
    );

    close(id);
  });

  const handleDelete = () => {
    if (!category) return;

    modalsService.openModal(ConfirmationModal, {
      question: t("approval:category_delete_title"),
      text: t("approval:category_delete_description"),
      acceptTitle: t("common:delete"),
      onAccept: async () => {
        await categoryDelete.mutateAsync(category.id);
        analyticsService.trackEvent(analyticEvents.approvals.categoryDeleted, {
          [analyticProperties.id]: category.id,
        });
        close(id);
      },
    });
  };

  return (
    <Modal
      {...props}
      title={t(isEdit ? "approval:category_edit_title" : "approval:category_create_title")}
      withCloser
      actions={
        <Stack gap={"sm"}>
          <ServerErrorField errors={errors} />
          <Button type={"submit"} size={"lg"} isLoading={categorySave.isPending} onClick={onSubmit}>
            {t("common:save")}
          </Button>
        </Stack>
      }
    >
      <form onSubmit={onSubmit}>
        <Stack gap={"md"}>
          <Input
            label={t("common:title")}
            required={true}
            autoFocus={true}
            type="text"
            {...register("title")}
            error={errors.title?.message}
          />
          <Textarea
            label={t("common:description")}
            {...register("description")}
            error={errors.description?.message}
          />
          {isEdit && (
            <DeleteListItem title={t("approval:category_delete_title")} onDelete={handleDelete} />
          )}
        </Stack>
      </form>
    </Modal>
  );
}
